import { Controller } from "react-hook-form";
import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useAddVehicleOperationForm from "./useAddVehicleOperationForm";

export const addVehicleOperationPageRouteName = "/config/vehicle-operation-config/add";

const AddVehicleOperationPage = () => {
  const {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    showAttachment,
    vehicleModelOptions,
    expandedItems,
    toggleExpand,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    t,
    vehicleTypes,
    fuelTypes,
    tripTypes,
    attachmentOptions,
  } = useAddVehicleOperationForm();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Left Panel - Form */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("section-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {editingIndex !== null
                ? t("section-subtitle-edit")
                : t("section-subtitle-add")}
            </Typography>
          </div>

          <hr className="border-border mb-6" />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddToList();
            }}
            className="flex flex-col gap-4"
          >
            {/* Vehicle Type */}
            <Controller
              name="vehicleTypeId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-vehicle-type")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={vehicleTypes.map((vt) => ({
                      label: vt.name ?? "",
                      value: vt.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-vehicle-type")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-operation-vehicle-type"
                  />
                </div>
              )}
            />

            {/* Vehicle Attachment (conditional) */}
            {showAttachment && (
              <Controller
                name="vehicleAttachmentIds"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-vehicle-attachment")}
                    </Typography>
                    <Select
                      options={attachmentOptions.map((a) => ({
                        label: `${a.name}`,
                        value: a.id,
                        description: undefined,
                      }))}
                      value={field.value?.join(",") || ""}
                      placeholder={t("label-vehicle-attachment")}
                      onValueChange={(val) => {
                        const current = field.value || [];
                        const valStr = val as string;
                        if (current.includes(valStr)) {
                          field.onChange(current.filter((v) => v !== valStr));
                        } else {
                          field.onChange([...current, valStr]);
                        }
                      }}
                      data-testid="multiselect-operation-attachment"
                    />
                    {/* Display selected tags */}
                    {field.value && field.value.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {field.value.map((attId) => {
                          const att = attachmentOptions.find((a) => a.id === attId);
                          return att ? (
                            <span
                              key={attId}
                              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs text-primary-700"
                            >
                              {att.name}
                              <button
                                type="button"
                                onClick={() =>
                                  field.onChange(field.value.filter((v) => v !== attId))
                                }
                                className="ml-1 text-primary-500 hover:text-primary-700"
                                data-testid={`btn-remove-attachment-operation-${attId}`}
                              >
                                <Icon name="close" size={12} />
                              </button>
                              <span className="text-greyScale-40 text-[10px]"></span>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}
              />
            )}

            {/* Vehicle Model */}
            <Controller
              name="vehicleModelId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-vehicle-model")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={vehicleModelOptions.map((vm) => ({
                      label: vm.name ?? "",
                      value: vm.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-vehicle-model")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-operation-vehicle-model"
                  />
                </div>
              )}
            />

            {/* Fuel Type */}
            <Controller
              name="fuelTypeId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-fuel-type")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={fuelTypes.map((ft) => ({
                      label: ft.name ?? "",
                      value: ft.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-fuel-type")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-operation-fuel-type"
                  />
                </div>
              )}
            />

            {/* Trip Type */}
            <Controller
              name="tripTypeId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-trip-type")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={tripTypes.map((tt) => ({
                      label: tt.name ?? "",
                      value: tt.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-trip-type")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-operation-trip-type"
                  />
                </div>
              )}
            />

            {/* Fuel Usage & Avg Speed - inline */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Typography variant="bodyLarge">
                  {t("label-fuel-usage")}{" "}
                  <span className="text-error-500">*</span>
                </Typography>
                <Input
                  type="number"
                  placeholder="0"
                  step="any"
                  {...register("fuelUsage")}
                  error={errors.fuelUsage}
                  data-testid="input-operation-fuel-usage"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Typography variant="bodyLarge">
                  {t("label-avg-speed")}{" "}
                  <span className="text-error-500">*</span>
                </Typography>
                <Input
                  type="number"
                  placeholder="0"
                  step="any"
                  {...register("avgSpeed")}
                  error={errors.avgSpeed}
                  data-testid="input-operation-avg-speed"
                />
              </div>
            </div>

            {/* Working Hours */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                Working Hours{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <Input
                type="number"
                placeholder="0"
                step="any"
                {...register("workingHours")}
                error={errors.workingHours}
                data-testid="input-operation-working-hours"
              />
            </div>

            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-status")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={[
                      { label: t("status-active"), value: "active" },
                      { label: t("status-inactive"), value: "inactive" },
                    ]}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-operation-status"
                  />
                </div>
              )}
            />

            {/* Add / Edit buttons */}
            {editingIndex !== null ? (
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  color="tertiary"
                  size="medium"
                  isFullSize
                  onClick={handleDiscardChanges}
                  data-testid="btn-discard-changes-operation"
                >
                  {t("btn-discard-changes")}
                </Button>
                <Button
                  type="submit"
                  variant="fill"
                  color="primary"
                  size="medium"
                  isFullSize
                  data-testid="btn-save-changes-operation"
                >
                  {t("btn-save-changes")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="fill"
                color="primary"
                size="medium"
                isFullSize
                data-testid="btn-add-more-operation"
              >
                {t("btn-add-to-list")}
              </Button>
            )}
          </form>
        </section>

        {/* Right Panel - Items List */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("list-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {t("list-subtitle")}
            </Typography>
          </div>

          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="document-text"
                size={48}
                className="text-greyScale-60 mb-4"
              />
              <Typography variant="body" tone="muted">
                {t("no-data-title")}
              </Typography>
              <Typography variant="body" tone="muted">
                {t("no-data-subtitle")}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[600px] overflow-y-auto">
              {fields.map((item, index) => {
                const isExpanded = expandedItems[index] ?? false;
                return (
                  <div
                    key={item.id}
                    className={`rounded-xl border p-4 ${
                      editingIndex === index
                        ? "border-primary-500 bg-primary-50"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex flex-col gap-1 flex-1">
                        <Typography variant="bodyLarge" className="font-semibold">
                          {item.vehicleTypeName}
                        </Typography>

                        {/* Always visible fields */}
                        <div className="flex items-center gap-2">
                          <Typography variant="body">{t("label-vehicle-model")}</Typography>
                          <Typography variant="body">:</Typography>
                          <Typography variant="body" tone="muted">{item.vehicleModelName}</Typography>
                        </div>
                        <div className="flex items-center gap-2">
                          <Typography variant="body">FuelType</Typography>
                          <Typography variant="body">:</Typography>
                          <Typography variant="body" tone="muted">{item.fuelTypeName}</Typography>
                        </div>
                        <div className="flex items-center gap-2">
                          <Typography variant="body">{t("label-trip-type")}</Typography>
                          <Typography variant="body">:</Typography>
                          <Typography variant="body" tone="muted">{item.tripTypeName}</Typography>
                        </div>
                        <div className="flex items-center gap-2">
                          <Typography variant="body">Fuel Usage</Typography>
                          <Typography variant="body">:</Typography>
                          <Typography variant="body" tone="muted">{item.fuelUsage} Km/L</Typography>
                        </div>

                        {/* Expandable fields */}
                        {isExpanded && (
                          <>
                            <div className="flex items-center gap-2">
                              <Typography variant="body">Av. Speed</Typography>
                              <Typography variant="body">:</Typography>
                              <Typography variant="body" tone="muted">{item.avgSpeed} Km/Jam</Typography>
                            </div>
                            <div className="flex items-center gap-2">
                              <Typography variant="body">Working Hours</Typography>
                              <Typography variant="body">:</Typography>
                              <Typography variant="body" tone="muted">
                                {item.workingHours} Hours
                              </Typography>
                            </div>
                            {item.vehicleAttachmentNames.length > 0 && (
                              <div className="flex items-center gap-2">
                                <Typography variant="body">{t("label-vehicle-attachment")}</Typography>
                                <Typography variant="body">:</Typography>
                                <Typography variant="body" tone="muted">
                                  {item.vehicleAttachmentNames.join(", ")}
                                </Typography>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Typography variant="body">{t("label-status")}</Typography>
                              <Typography variant="body">:</Typography>
                              <Typography variant="body" tone="muted" className="capitalize">
                                {item.status}
                              </Typography>
                            </div>
                          </>
                        )}

                        {/* Show more / Show less */}
                        <button
                          type="button"
                          onClick={() => toggleExpand(index)}
                          className="text-primary-500 text-sm text-left mt-1 cursor-pointer hover:underline"
                          data-testid={`btn-toggle-expand-operation-${index}`}
                        >
                          {isExpanded ? t("show-less") : t("show-more")} &#8593;
                        </button>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-3 ml-4">
                        <button
                          type="button"
                          onClick={() => handleEditItem(index)}
                          className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer border ${
                            editingIndex === index
                              ? "bg-primary-500 text-white border-primary-500"
                              : "hover:bg-greyScale-90 text-primary-500 border-primary-500"
                          }`}
                          data-testid={`btn-edit-row-operation-${index}`}
                        >
                          <Icon name="edit-2" size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(index)}
                          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-error-50 text-error-500 transition-colors cursor-pointer border border-error-500"
                          data-testid={`btn-delete-row-operation-${index}`}
                        >
                          <Icon name="trash" size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={handleCancel}
          data-testid="btn-cancel-operation"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={() => setShowSaveModal(true)}
          disabled={fields.length === 0}
          data-testid="btn-save-operation"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleSave}
        onCancel={() => setShowSaveModal(false)}
        testId="btn-save-confirmation-modal-operation"
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-cancel-title")}
        message={t("modal-cancel-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="btn-cancel-confirmation-modal-operation"
      />
    </div>
  );
};

export default AddVehicleOperationPage;
